import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAppState } from '../../../store/appstate';
import userSettingsActions from '../../../store/userSettings/actions';
import { Log } from '../../../types/log/Log';
import Spinner from '../../atoms/Spinner/Spinner';
import CardBody from '../../atoms/Card/CardBody';
import ExpandableCard from '../../atoms/Card/ExpandableCard';
import useTableExpanded from '../../../services/hooks/useTableExpanded';
import ErrorMessage from '../../atoms/Message/Error/ErrorMessage';
import InformationMessage from '../../atoms/Message/Information/InformationMessage';
import { ErrorType } from '../../../types/response/ErrorCodes';
import TableHeaderTypes from '../../../types/tableHeaderType';
import { useLogsStore } from '../../../store/logs/logsStore';
import SpinnerWrapper from '../../atoms/Spinner/SpinnerWrapper';
import { generateColumnString, parseSearchUrl } from '../../../services/helpers/queryStringHelpers';
import { browsePagination } from '../../../services/helpers/pagination';
import { useAppDispatch } from '../../../store';
import Table from '../../atoms/Table/Table';
import { ColumnDef } from '../../atoms/Table/tableFeatures';
import { formatDate, formatTimeMilliSeconds } from '../../../services/helpers/dateTimeFormats';
import TwoRowCell from '../../atoms/Table/TwoRowCell';
import StatusTypes from '../../../types/StatusTypes';
import Status from '../../atoms/Status/Status';
import { checkMatchByLowercase, getHeaderColumns } from '../../../services/helpers/columnFilter';
import { TypedColumn } from '../../../types/TypedColumn';
import { Modal } from '../../atoms/Modal/Modal';
import useModal from '../../../services/hooks/useModal';
import LogDetails from './LogDetails';
import { SearchForm } from './SearchForm';
import SelectOrganization from '../Admin/SelectOrganization';
import { listOrganizations } from '../../../api/organizations/organizations';
import { useOrganizationsStore } from '../../../store/admin/organizations/organizationsStore';
import { SessionState } from '../../../store/session/sessionSlice';
import HeaderContent from '../../atoms/Layout/HeaderContent';
import CenterContent from '../../atoms/Layout/CenterContent';
import useLogTimeSpentOnPage from '../../../services/hooks/useLogTimeSpentOnPage';
import Flex from '../../atoms/Box/Flex';

interface StyleProps {
    isSearching: boolean;
}

export const LogsSearch = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const logDetailsModal = useModal();
    const [currentLog, setCurrentLog] = useState<Log>();
    const sessionColumns = useAppState<string[]>(s => s.userSettings.logColumnFilters) ?? [];
    const { pathname, search: queryString } = useLocation();
    const { user } = useAppState<SessionState>(s => s.session);
    const { getOrganizationsSucceeded, getOrganizationsFailed } = useOrganizationsStore();
    const [selectedLogOrgId, setSelectedLogOrgId] = useState<string>(user?.isInternal ? 'all' : '');
    useLogTimeSpentOnPage('developerLogs');
    const { isTableExpanded, toggleTableExpanded } = useTableExpanded();

    useEffect(() => {
        if (user?.isInternal) {
            listOrganizations(getOrganizationsSucceeded, getOrganizationsFailed);
        }
    }, [user?.isInternal]);

    const defaultColumns = user?.isInternal
        ? ['Timestamp', 'Status', 'Method', 'Path', 'Organization']
        : ['Timestamp', 'Status', 'Method', 'Path'];
    const allColumnHeaders: ColumnDef<Log>[] = [
        {
            header: 'Timestamp',
            accessorKey: 'timestamp',
            cell: (props: { row: { original: Log } }) => {
                const log = props.row.original;
                const date = formatDate(new Date(log.timestamp));
                const time = formatTimeMilliSeconds(new Date(log.timestamp));
                return <TwoRowCell upper={date.yyyyMMdd} lower={time} />;
            },
        },
        {
            header: 'Status',
            accessorKey: 'statusCode',
            cell: (props: { row: { original: Log } }) => {
                const { statusCode } = props.row.original;
                return (
                    Number(statusCode) !== 0 && (
                        <Status type={StatusTypes.HTTP} status={statusCode} />
                    )
                );
            },
        },
        {
            header: 'Method',
            accessorKey: 'httpMethod',
            cell: (props: { row: { original: Log } }) => {
                return <span>{props.row.original.httpMethod}</span>;
            },
        },
        {
            header: 'Path',
            accessorKey: 'requestPath',
            cell: (props: { row: { original: Log } }) => {
                return <span>{props.row.original.requestPath}</span>;
            },
        },
        {
            header: 'Organization',
            accessorKey: 'organizationName',
            cell: (props: { row: { original: Log } }) => {
                return <span>{props.row.original.organizationName}</span>;
            },
        },
    ];

    const { perPage, columns } = parseSearchUrl(queryString, sessionColumns);

    const columnString = generateColumnString(columns);

    const getColumns = () => {
        const startingColumns = allColumnHeaders.filter(x =>
            defaultColumns.includes(x.header as string)
        );

        const queryFilterColumns = columns.map(x =>
            allColumnHeaders.find(
                y => y.header === x || checkMatchByLowercase(y.header as string, x)
            )
        ) as TypedColumn<Log>[];

        const isInvalidArray = queryFilterColumns.some(x => x === undefined);

        const tableColumns =
            queryFilterColumns.length !== 0 && !isInvalidArray
                ? queryFilterColumns
                : startingColumns;

        const allColumnsAsStrings = allColumnHeaders.map(x => x.header);
        const isSessionColumnsValid = sessionColumns.every(col =>
            allColumnsAsStrings.includes(col)
        );

        if (
            tableColumns.length !== sessionColumns.length &&
            columns &&
            columns.length === 0 &&
            isSessionColumnsValid
        ) {
            const savedColumns = getHeaderColumns(sessionColumns, allColumnHeaders);
            return savedColumns;
        }

        return tableColumns;
    };

    const {
        isSearching,
        isCanceled,
        isFailed,
        searchResult: { links: logLinks, metaData: logMetaData, data: logData = [] },
        error,
    } = useLogsStore();

    const onRowClick = (row: Log) => {
        setCurrentLog(row);
        logDetailsModal.toggle();
    };

    const onCloseLogDetailsModal = () => {
        logDetailsModal.toggle();
    };

    useEffect(() => {
        if (sessionColumns.length === 0) {
            dispatch(userSettingsActions.saveColumnFilters(defaultColumns, TableHeaderTypes.Log));
        }
    }, []);

    return (
        <>
            {currentLog && (
                <Modal mediumLarge isShown={logDetailsModal.isShown} hide={onCloseLogDetailsModal}>
                    <LogDetails log={currentLog} hide={onCloseLogDetailsModal} />
                </Modal>
            )}
            <Page>
                <HeaderContent>
                    <CenterContent>
                        <Flex row justifyContent="space-between">
                            <SearchForm organizationId={selectedLogOrgId} />

                            <Actions>
                                <SelectOrganization
                                    includeAllOption
                                    value={selectedLogOrgId}
                                    onChange={setSelectedLogOrgId}
                                />
                            </Actions>
                        </Flex>
                    </CenterContent>
                </HeaderContent>
                <Margin />
                <CenterContent>
                    <ExpandableCard expanded={isTableExpanded}>
                        <CardBody>
                            {isFailed ? (
                                <MessageWrapper>
                                    <ErrorMessage error={error} errorHeader={ErrorType.Search} />
                                </MessageWrapper>
                            ) : isSearching || isCanceled ? (
                                <SpinnerWrapper>
                                    <Spinner
                                        text="Searching..."
                                        loading={isSearching || isCanceled}
                                    />
                                </SpinnerWrapper>
                            ) : logMetaData && logMetaData.totalNumberOfRecords > 0 ? (
                                <TableWrapper isSearching={isSearching || isCanceled}>
                                    <Table<Log>
                                        data={logData}
                                        allColumns={allColumnHeaders}
                                        columns={getColumns() as ColumnDef<Log>[]}
                                        setColumns={_ => {}}
                                        defaultColumns={defaultColumns}
                                        tableHeaderType={TableHeaderTypes.Log}
                                        pagination={{
                                            metaData: logMetaData,
                                            links: logLinks,
                                            pageSize: perPage,
                                            nextPage: () =>
                                                browsePagination(
                                                    navigate,
                                                    pathname,
                                                    `${logLinks.next?.query}${columnString}`
                                                ),
                                            previousPage: () =>
                                                browsePagination(
                                                    navigate,
                                                    pathname,
                                                    `${logLinks.prev?.query}${columnString}`
                                                ),
                                        }}
                                        onRowClicked={onRowClick}
                                        columnMenu
                                        isExpanded={isTableExpanded}
                                        onExpandToggle={toggleTableExpanded}
                                    />
                                </TableWrapper>
                            ) : (
                                <MessageWrapper>
                                    <InformationMessage
                                        messages={['Please try a different search query.']}
                                        messageHeader="No logs found for the past 30 days"
                                    />
                                </MessageWrapper>
                            )}
                        </CardBody>
                    </ExpandableCard>
                </CenterContent>
            </Page>
        </>
    );
};

const Page = styled.div`
    padding-bottom: 4rem;
`;

const Margin = styled.div`
    margin-top: 2rem;
`;

const TableWrapper = styled.div<StyleProps>`
    opacity: ${props => (props.isSearching ? 0.5 : 1)};
`;

const MessageWrapper = styled.div`
    margin: 2rem;
    padding-top: 1rem;
`;

const Col = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 ${props => props.theme.layout.padding.medium};
    border-right: 0.2rem solid ${props => props.theme.colors.text.subtle};
    &:first-child {
        padding-left: 0;
    }
    &:last-child {
        padding-right: 0;
        border-right: 0;
    }
`;

const Actions = styled.div`
    margin-left: auto;
    display: inline-flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: center;
`;
